import React, { useEffect, useState, useRef } from "react";
import backendVerifiedUser from "../../../api/backendVerifiedUser";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";

// Layouts that support light/dark mode
const MODE_LAYOUTS = ["modern", "fancy"];
const LAYOUT_LABELS = { classic: "Classic", modern: "Modern", fancy: "Fancy" };

const LAYOUT_OPTIONS = [
  { label: "Classic", value: "classic" },
  { label: "Modern", value: "modern" },
  { label: "Fancy", value: "fancy" },
];

export default function Themes() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [previewHtml, setPreviewHtml] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [formName, setFormName] = useState("");
  const [formLayout, setFormLayout] = useState("classic");
  const [formMode, setFormMode] = useState("light");
  const [formColorPrimary, setFormColorPrimary] = useState("6366f1");
  const [formColorSecondary, setFormColorSecondary] = useState("f97316");
  const toast = useRef(null);

  const openCreateForm = () => {
    setEditingTheme(null);
    setFormName("");
    setFormLayout("classic");
    setFormMode("light");
    setFormColorPrimary("6366f1");
    setFormColorSecondary("f97316");
    setShowForm(true);
  };

  const openEditForm = (theme) => {
    setEditingTheme(theme);
    setFormName(theme.name || "");
    setFormLayout(theme.layout_key || "classic");
    setFormMode(theme.mode || "light");
    setFormColorPrimary((theme.colorPrimary || "6366f1").replace(/^#/, ""));
    setFormColorSecondary((theme.colorSecondary || "f97316").replace(/^#/, ""));
    setShowForm(true);
  };

  const saveTheme = async () => {
    const body = {
      name: formName,
      layout_key: formLayout,
      mode: formMode,
      colorPrimary: formColorPrimary.replace(/^#/, ""),
      colorSecondary: formColorSecondary.replace(/^#/, ""),
    };
    try {
      if (editingTheme) {
        await backendVerifiedUser.put(`/themes/${editingTheme.id}`, body);
      } else {
        await backendVerifiedUser.post("/themes", body);
      }
      toast.current?.show({ severity: "success", summary: "Saved", detail: "Theme saved" });
      setShowForm(false);
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Save failed" });
    }
  };

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const resp = await backendVerifiedUser.get("/themes");
      setThemes(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load themes" });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchThemes();
  }, []);


  const activate = async (id) => {
    try {
      await backendVerifiedUser.put(`/themes/${id}/activate`);
      toast.current?.show({ severity: "success", summary: "Activated", detail: "Theme activated" });
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Activation failed" });
    }
  };

  const uploadAsset = async (themeId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("asset", file);
    try {
      await backendVerifiedUser.post(`/themes/${themeId}/upload-assets`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.current?.show({ severity: "success", summary: "Uploaded", detail: "Asset uploaded" });
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Upload failed" });
    }
  };

  const previewTheme = (theme) => {
    setPreviewHtml("");
    if (!theme) return;

    const primary = `#${(theme.colorPrimary || "6366f1").replace(/^#/, "")}`;
    const secondary = `#${(theme.colorSecondary || "f97316").replace(/^#/, "")}`;
    // Fancy starts as a copy of Modern, so it shares the modern preview mock
    const isModern = MODE_LAYOUTS.includes(theme.layout_key);
    const layoutLabel = LAYOUT_LABELS[theme.layout_key] || "Modern";

    const mockHtml = isModern
      ? `
      <div class="preview-root">
        <header class="preview-header p-4" style="background:${primary}; color:#fff; display:flex; align-items:center; justify-content:space-between; border-radius:10px 10px 0 0;">
          <h1 style="margin:0; font-size:1.1rem;">Store Logo</h1>
          <div style="display:flex; gap:14px; font-size:0.85rem;"><span>Shop</span><span>Categories</span><span>Cart</span></div>
        </header>
        <main class="preview-main p-6" style="background:#fafafa;">
          <div style="background:${secondary}; color:#fff; border-radius:12px; padding:24px; margin-bottom:16px;">
            <h2 style="margin:0 0 6px;">${layoutLabel} Hero Banner</h2>
            <p style="margin:0; opacity:0.9;">Rounded, card-driven layout with bold banner.</p>
          </div>
          <div class="preview-cards grid grid-cols-3 gap-4">
            <div class="card p-4" style="border-radius:12px; box-shadow:0 2px 8px rgba(0,0,0,0.08);">Product 1</div>
            <div class="card p-4" style="border-radius:12px; box-shadow:0 2px 8px rgba(0,0,0,0.08);">Product 2</div>
            <div class="card p-4" style="border-radius:12px; box-shadow:0 2px 8px rgba(0,0,0,0.08);">Product 3</div>
          </div>
        </main>
        <footer class="preview-footer p-4" style="background:#1f2937; color:#d1d5db; border-radius:0 0 10px 10px;">Site Footer</footer>
      </div>
    `
      : `
      <div class="preview-root">
        <header class="preview-header p-4" style="background:${primary}; color:#fff; border-radius:4px 4px 0 0;">
          <h1 style="margin:0; font-size:1.1rem;">Site Header</h1>
        </header>
        <main class="preview-main p-6" style="background:#fff; border-left:1px solid #eee; border-right:1px solid #eee;">
          <h2 style="color:${secondary}; margin-top:0;">Hero / Banner</h2>
          <p>Traditional boxed layout with classic navigation.</p>
          <div class="preview-cards grid grid-cols-3 gap-4 mt-4">
            <div class="card p-4" style="border:1px solid #eee; border-radius:4px;">Card 1</div>
            <div class="card p-4" style="border:1px solid #eee; border-radius:4px;">Card 2</div>
            <div class="card p-4" style="border:1px solid #eee; border-radius:4px;">Card 3</div>
          </div>
        </main>
        <footer class="preview-footer p-4" style="background:#f3f4f6; border-radius:0 0 4px 4px;">Site Footer</footer>
      </div>
    `;
    setPreviewHtml(mockHtml);
    document.getElementById('theme-preview')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="mx-4">
      <Toast ref={toast} />

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold">Available Themes</h3>
          <Button label="New Theme" icon="pi pi-plus" className="p-button-sm" onClick={openCreateForm} />
        </div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {themes.length === 0 && <div style={{ color: '#6b7280' }}>No themes found</div>}
            {themes.map((t) => {
              const files = t.files || [];
              const thumbnailFile = files.find(f => /\.(png|jpe?g|webp|gif|svg)$/i.test(f.name));
              const thumbnail = thumbnailFile ? thumbnailFile.url : null;
              const isActive = t.isActive === 1;
              const primaryHex = `#${(t.colorPrimary || '6366f1').replace(/^#/, '')}`;
              const secondaryHex = `#${(t.colorSecondary || 'f97316').replace(/^#/, '')}`;
              return (
                <div
                  key={t.id}
                  style={{
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: '#fff',
                    border: isActive ? '2px solid #22c55e' : '1px solid #e5e7eb',
                    boxShadow: isActive ? '0 4px 16px rgba(34,197,94,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div style={{ position: 'relative', width: '100%', height: 150, overflow: 'hidden' }}>
                    {thumbnail ? (
                      <img src={thumbnail} alt={t.name || `Theme ${t.id}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          background: `linear-gradient(135deg, ${primaryHex} 0%, ${secondaryHex} 100%)`,
                        }}
                      >
                        <i className={t.layout_key === 'fancy' ? 'pi pi-star' : t.layout_key === 'modern' ? 'pi pi-th-large' : 'pi pi-table'} style={{ color: '#fff', fontSize: 22, opacity: 0.9 }} />
                        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.25)' }}>
                          {LAYOUT_LABELS[t.layout_key] || 'Classic'} Layout
                        </span>
                      </div>
                    )}
                    {isActive && (
                      <span style={{
                        position: 'absolute', top: 10, right: 10, padding: '3px 10px',
                        background: '#22c55e', color: '#fff', fontSize: 11, fontWeight: 700,
                        borderRadius: 20, letterSpacing: 0.3, boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}>
                        ● ACTIVE
                      </span>
                    )}
                  </div>

                  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name || `Theme ${t.id}`}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <span title={`Primary ${primaryHex}`} style={{ width: 14, height: 14, borderRadius: '50%', background: primaryHex, border: '1px solid rgba(0,0,0,0.1)' }} />
                        <span title={`Secondary ${secondaryHex}`} style={{ width: 14, height: 14, borderRadius: '50%', background: secondaryHex, border: '1px solid rgba(0,0,0,0.1)' }} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <span style={{
                        display: 'inline-block', width: 'fit-content',
                        padding: '2px 8px', background: '#eef2ff', color: '#4338ca',
                        borderRadius: 6, fontSize: 11, fontWeight: 600,
                      }}>
                        {LAYOUT_LABELS[t.layout_key] || 'Classic'} layout
                      </span>
                      {MODE_LAYOUTS.includes(t.layout_key) && (
                        <span style={{
                          display: 'inline-block', width: 'fit-content',
                          padding: '2px 8px',
                          background: t.mode === 'dark' ? '#1f2937' : '#fef9c3',
                          color: t.mode === 'dark' ? '#e5e7eb' : '#854d0e',
                          borderRadius: 6, fontSize: 11, fontWeight: 600,
                        }}>
                          {t.mode === 'dark' ? '🌙 Dark' : '☀️ Light'}
                        </span>
                      )}
                    </div>

                    {t.meta?.description && (
                      <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 6 }}>{t.meta.description}</div>
                    )}

                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
                      {isActive ? (
                        <button disabled style={{
                          flex: 1, padding: '8px 0', borderRadius: 7, border: 'none',
                          background: '#dcfce7', color: '#16a34a', fontWeight: 600, fontSize: 13, cursor: 'default',
                        }}>
                          <i className="pi pi-check" style={{ fontSize: 12, marginRight: 6 }} />Active
                        </button>
                      ) : (
                        <button onClick={() => activate(t.id)} style={{
                          flex: 1, padding: '8px 0', borderRadius: 7, border: 'none',
                          background: '#4f46e5', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                        }}>
                          Activate
                        </button>
                      )}
                      <button onClick={() => openEditForm(t)} title="Edit" style={{
                        width: 36, height: 36, borderRadius: 7, border: '1px solid #e5e7eb',
                        background: '#fff', color: '#374151', cursor: 'pointer', flexShrink: 0,
                      }}>
                        <i className="pi pi-pencil" style={{ fontSize: 13 }} />
                      </button>
                      <button onClick={() => previewTheme(t)} title="Preview" style={{
                        width: 36, height: 36, borderRadius: 7, border: '1px solid #e5e7eb',
                        background: '#fff', color: '#374151', cursor: 'pointer', flexShrink: 0,
                      }}>
                        <i className="pi pi-eye" style={{ fontSize: 13 }} />
                      </button>
                      <label title="Upload asset" style={{ flexShrink: 0 }}>
                        <input
                          type="file"
                          accept="image/*, text/css, .css, .zip"
                          onChange={(e) => uploadAsset(t.id, e.target.files[0])}
                          style={{ display: 'none' }}
                          id={`asset-${t.id}`}
                        />
                        <span
                          onClick={() => document.getElementById(`asset-${t.id}`).click()}
                          style={{
                            width: 36, height: 36, borderRadius: 7, border: '1px solid #e5e7eb',
                            background: '#fff', color: '#374151', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <i className="pi pi-upload" style={{ fontSize: 13 }} />
                        </span>
                      </label>
                    </div>

                    <button
                      onClick={() => setExpanded(prev => ({ ...prev, [t.id]: !prev[t.id] }))}
                      style={{
                        marginTop: 8, alignSelf: 'flex-start', border: 'none', background: 'transparent',
                        color: '#6366f1', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0,
                      }}
                    >
                      {expanded[t.id] ? 'Hide details ▲' : 'Show details ▼'}
                    </button>
                    {expanded[t.id] && (
                      <div style={{ marginTop: 8, fontSize: 11.5, color: '#6b7280', background: '#f9fafb', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div>ID: {t.id}</div>
                        <div>Folder: {t.folder_name || '-'}</div>
                        <div>Asset: {t.asset_path ? <a href={t.asset_path} target="_blank" rel="noreferrer" style={{ color: '#4f46e5' }}>Open</a> : '-'}</div>
                        <div>Created: {t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</div>
                        <div>Updated: {t.updated_at ? new Date(t.updated_at).toLocaleString() : '-'}</div>
                        {files.length > 0 && (
                          <div>
                            Files:{' '}
                            {files.map((f) => (
                              <a key={f.url} href={f.url} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', marginRight: 8 }}>
                                {f.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div id="theme-preview" className="card p-4 mt-4">
        <h3 className="text-lg font-bold mb-3">Preview</h3>
        <div className="border p-3">
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      </div>

      <Dialog
        header={editingTheme ? "Edit Theme" : "New Theme"}
        visible={showForm}
        style={{ width: "30rem" }}
        onHide={() => setShowForm(false)}
      >
        <div className="flex flex-col gap-4 pt-2">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <InputText
              className="w-full"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Theme name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Layout</label>
            <Dropdown
              className="w-full"
              value={formLayout}
              options={LAYOUT_OPTIONS}
              onChange={(e) => setFormLayout(e.value)}
            />
          </div>
          {MODE_LAYOUTS.includes(formLayout) && (
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="theme-mode" className="block text-sm font-medium">Dark Mode</label>
                <span className="text-xs text-gray-500">{LAYOUT_LABELS[formLayout]} layout can render light or dark</span>
              </div>
              <InputSwitch
                id="theme-mode"
                checked={formMode === "dark"}
                onChange={(e) => setFormMode(e.value ? "dark" : "light")}
              />
            </div>
          )}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={`#${formColorPrimary.replace(/^#/, '')}`}
                  onChange={(e) => setFormColorPrimary(e.target.value.replace(/^#/, ''))}
                  style={{ width: 40, height: 36, padding: 0, border: '1px solid #dee2e6', borderRadius: 4, cursor: 'pointer' }}
                />
                <InputText
                  className="w-full"
                  value={formColorPrimary}
                  onChange={(e) => setFormColorPrimary(e.target.value.replace(/^#/, ''))}
                  placeholder="6366f1"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={`#${formColorSecondary.replace(/^#/, '')}`}
                  onChange={(e) => setFormColorSecondary(e.target.value.replace(/^#/, ''))}
                  style={{ width: 40, height: 36, padding: 0, border: '1px solid #dee2e6', borderRadius: 4, cursor: 'pointer' }}
                />
                <InputText
                  className="w-full"
                  value={formColorSecondary}
                  onChange={(e) => setFormColorSecondary(e.target.value.replace(/^#/, ''))}
                  placeholder="f97316"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setShowForm(false)} />
            <Button label="Save" onClick={saveTheme} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
